import { useIntl } from "react-intl";
import { Disclosure } from "@/components/design/Disclosure";
import { fromStoragePercent, toStoragePercent } from "@/lib/skill-levels";
import { newCondition, type ConditionByTypename, type MatchCondition } from "../design-codec";
import type { MatchData } from "../match-design-types";
import {
  MatchSkillsDiagram,
  type MatchSkillEntry,
  type MatchSkillRequirement,
} from "./MatchSkillsDiagram";

// Bewerbungs-Modus: benötigtes Fachwissen als 2D-Raster (Stufe × Muss/Kann) über die
// match-eigene MatchSkillsDiagram. Skill + Mindeststufe + Muss/Kann stecken in der
// Zelle, in die das Chip gezogen wird. Gebunden an die ExpertiseConditions des Match-Designs.
type ExpertiseCondition = ConditionByTypename<"ExpertiseCondition">;

export function RecruitingExpertise({
  data,
  update,
  readOnly = false,
  className = "",
}: {
  data: MatchData;
  update: (patch: Partial<MatchData>) => void;
  readOnly?: boolean;
  className?: string;
}) {
  const intl = useIntl();

  const expertise = data.conditions.filter(
    (c): c is ExpertiseCondition => c.__typename === "ExpertiseCondition",
  );

  const entries: MatchSkillEntry[] = expertise.map((c, i) => ({
    id: `req-exp-${i}`,
    description: c.expertise,
    level: fromStoragePercent(Number(c.level)),
    requirement: c.requirement,
  }));

  function writeExpertise(next: ExpertiseCondition[]) {
    const others = data.conditions.filter((c) => c.__typename !== "ExpertiseCondition");
    update({ conditions: [...others, ...next] as MatchCondition[] });
  }
  const indexOf = (id: string) => Number(id.replace("req-exp-", ""));
  function replaceAt(i: number, cond: ExpertiseCondition) {
    const next = expertise.slice();
    next[i] = cond;
    writeExpertise(next);
  }

  function onPlace(id: string, level: number) {
    const i = indexOf(id);
    const cur = expertise[i];
    if (!cur) return;
    replaceAt(i, { ...cur, level: level === 0 ? 0 : toStoragePercent(level) });
  }
  function onToggleRequirement(id: string, requirement: MatchSkillRequirement) {
    const i = indexOf(id);
    const cur = expertise[i];
    if (!cur) return;
    replaceAt(i, { ...cur, requirement });
  }
  function onAdd(description: string, level: number, requirement: MatchSkillRequirement) {
    // newCondition setzt requirement auf den Default OPTIONAL; der Add-Editor kann Muss mitgeben.
    const created = newCondition("ExpertiseCondition", {
      expertise: description,
      level: level === 0 ? 0 : toStoragePercent(level),
      aiMode: "NO_AI",
      operator: "GE",
    });
    writeExpertise([...expertise, { ...created, requirement }]);
  }
  function onRename(id: string, description: string) {
    const i = indexOf(id);
    const cur = expertise[i];
    if (!cur) return;
    replaceAt(i, { ...cur, expertise: description });
  }
  function onDelete(id: string) {
    const i = indexOf(id);
    if (!expertise[i]) return;
    const next = expertise.slice();
    next.splice(i, 1);
    writeExpertise(next);
  }

  return (
    <Disclosure
      className={className}
      title={intl.formatMessage({ id: "match.recruiting.expertiseTitle", defaultMessage: "Benötigtes Fachwissen (mind.)" })}
      defaultOpen
    >
      <MatchSkillsDiagram
        entries={entries}
        readOnly={readOnly}
        onPlace={onPlace}
        onToggleRequirement={onToggleRequirement}
        onAdd={onAdd}
        onRename={onRename}
        onDelete={onDelete}
      />
    </Disclosure>
  );
}
